import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AppEmptyState, AppSpinner } from '../ui';
import KslFrame from './KslFrame';
import { notify } from '../common/AppToast';
import klubService from '../../services/klubService';
import { useTableSort } from '../../hooks/useTableSort';
import { ROUTES } from '../../config/routes';
import { FORMULE_LABELS, formatDate } from '../../utils/klubUtils';
import {
  PHASE_ACTIFS,
  PHASE_INTEGRATION,
  PHASE_RESILIATION,
  isStepDone,
  nextRenewalIso,
  preavisDeadlineIso,
} from '../../utils/klubPipelinePhases';
import '../../styles/klub-dashboard.scss';

const STEP_FACE = {
  rdv_fab: { mini: 'RDV', mid: 'RDV fab', group: 'commercial', groupLabel: 'Commercial' },
  valide: { mini: 'BDC', mid: 'BDC', group: 'commercial', groupLabel: 'Commercial' },
  questionnaire_emis: { mini: 'Émis', mid: 'Émis', group: 'questionnaire', groupLabel: 'Questionnaire' },
  questionnaire_transmis: { mini: 'Transm.', mid: 'Transmis', group: 'questionnaire', groupLabel: 'Questionnaire' },
  questionnaire_complete: { mini: 'Récup.', mid: 'Récupéré', group: 'questionnaire', groupLabel: 'Questionnaire' },
  photos_recues: { mini: 'Photos', mid: 'Photos', group: 'production', groupLabel: 'Production' },
  site_a_creer: { mini: 'Site', mid: 'Site', group: 'production', groupLabel: 'Production' },
  bat_emis: { mini: 'BAT', mid: 'BAT', group: 'production', groupLabel: 'Production' },
  bat_valide: { mini: 'Live', mid: 'En ligne', group: 'live', groupLabel: 'Mise en ligne', hideDesktopLabel: true },
  ras: { mini: 'RAS', mid: 'RAS', group: 'actifs', groupLabel: 'Suivi' },
  resiliation_demandee: { mini: 'Demande', mid: 'Demandée', group: 'resiliation', groupLabel: 'Résiliation' },
  resiliation_programmee: { mini: 'Prog.', mid: 'Programmée', group: 'resiliation', groupLabel: 'Résiliation' },
  resiliation_effective: { mini: 'Résilié', mid: 'Résilié', group: 'resiliation', groupLabel: 'Résiliation' },
};

const STEP_DONE_LABEL = {
  rdv_fab: 'RDV fab pris',
  valide: 'BDC validé',
  questionnaire_emis: 'Questionnaire émis',
  questionnaire_transmis: 'Questionnaire transmis',
  questionnaire_complete: 'Questionnaire complété',
  photos_recues: 'Photos récupérées',
  site_a_creer: 'Site créé',
  bat_emis: 'BAT envoyé',
  bat_valide: 'Mis en ligne',
  resiliation_demandee: 'Résiliation demandée',
  resiliation_programmee: 'Résiliation programmée',
  resiliation_effective: 'Résilié',
};

function stepTip(step, done) {
  const label = done ? (STEP_DONE_LABEL[step.step_code] || step.label) : step.label;
  return `${label} · ${done ? 'OK' : 'KO'}`;
}

const TABLE_SORT_COLUMNS = {
  entreprise: { type: 'string', getValue: (row) => row.entreprise },
  commercial: { type: 'string', getValue: (row) => row.commercial?.nom },
  mise_en_ligne: { type: 'date', getValue: (row) => row.date_mise_en_ligne },
  renouvellement: { type: 'date', getValue: (row) => nextRenewalIso(row.date_renouvellement) },
  preavis_limite: { type: 'date', getValue: (row) => preavisDeadlineIso(row.date_renouvellement) },
  date_demande: { type: 'date', getValue: (row) => row.date_demande_resiliation },
  date_sortie: { type: 'date', getValue: (row) => row.date_sortie_programmee },
  date_resilie: { type: 'date', getValue: (row) => row.date_resilie },
  salaries: { type: 'number', getValue: (row) => row.nombre_salaries ?? -1 },
};

function compactLabel(label) {
  const text = (label ?? '').trim();
  if (text.length <= 7) return text || '—';
  return `${text.slice(0, 6)}.`;
}

function faceOf(step) {
  return STEP_FACE[step.step_code] ?? {
    mini: compactLabel(step.label),
    mid: step.label,
    group: 'etapes',
    groupLabel: 'Étapes',
  };
}

function groupSteps(steps) {
  const groups = [];
  steps.forEach((step) => {
    const face = faceOf(step);
    const last = groups[groups.length - 1];
    if (last && last.key === face.group) {
      last.steps.push(step);
    } else {
      groups.push({
        key: face.group,
        label: face.groupLabel,
        hideDesktopLabel: Boolean(face.hideDesktopLabel),
        steps: [step],
      });
    }
  });
  return groups;
}

function splitPhaseRows(groups) {
  if (groups.length <= 2) return [groups];
  const cut = Math.ceil(groups.length / 2);
  return [groups.slice(0, cut), groups.slice(cut)];
}

function dolibarrTitle(adherent) {
  const name = adherent.dolibarr_thirdparty_nom || '';
  const labels = {
    matched: 'À valider',
    validated: 'Validé',
    rejected: 'Rejeté',
  };
  const label = labels[adherent.dolibarr_match_status] || 'En attente';
  return name ? `${name} · ${label}` : label;
}

const KlubSiteList = ({
  adherents,
  loading,
  stepsByPhase = {},
  phase = '',
  listReturnTo,
  onChanged,
  defaultSort = null,
}) => {
  const [statusOverrides, setStatusOverrides] = useState({});
  const [pendingKeys, setPendingKeys] = useState(() => new Set());
  const showMiseEnLigne = phase === PHASE_ACTIFS || phase === PHASE_RESILIATION;
  const showResiliationDates = phase === PHASE_RESILIATION;
  const showSalaries = phase === PHASE_RESILIATION;

  const { sortedRows } = useTableSort(adherents, TABLE_SORT_COLUMNS, { defaultSort });

  useEffect(() => {
    setStatusOverrides({});
  }, [adherents]);

  const steps = useMemo(() => {
    if (phase === PHASE_ACTIFS) return [];
    const source = phase ? stepsByPhase[phase] : stepsByPhase[PHASE_INTEGRATION];
    return source ?? [];
  }, [phase, stepsByPhase]);

  const groups = useMemo(() => groupSteps(steps), [steps]);
  const phaseRows = useMemo(() => splitPhaseRows(groups), [groups]);

  const statusOf = (adherent, stepCode) => (
    statusOverrides[`${adherent.id}:${stepCode}`]
    ?? adherent.workflow_statuses?.[stepCode]
    ?? 'pending'
  );

  const koCounts = useMemo(() => {
    const counts = {};
    steps.forEach((step) => {
      counts[step.step_code] = sortedRows.filter(
        (row) => !isStepDone(statusOverrides[`${row.id}:${step.step_code}`] ?? row.workflow_statuses?.[step.step_code]),
      ).length;
    });
    return counts;
  }, [steps, sortedRows, statusOverrides]);

  const dateColumns = useMemo(() => {
    const columns = [];
    if (phase === PHASE_ACTIFS) {
      columns.push(
        {
          key: 'renouvellement',
          label: 'Renouvellement',
          title: 'Date de renouvellement',
          width: 124,
          get: (row) => nextRenewalIso(row.date_renouvellement),
        },
        {
          key: 'preavis_limite',
          label: 'Limite préavis',
          title: 'Date limite de préavis',
          width: 124,
          get: (row) => preavisDeadlineIso(row.date_renouvellement),
        },
      );
    }
    if (showMiseEnLigne) {
      columns.push({ key: 'mise_en_ligne', label: 'Mise en ligne', get: (row) => row.date_mise_en_ligne });
    }
    if (showResiliationDates) {
      columns.push(
        { key: 'date_demande', label: 'Demande', get: (row) => row.date_demande_resiliation },
        { key: 'date_sortie', label: 'Programmée', get: (row) => row.date_sortie_programmee },
        { key: 'date_resilie', label: 'Résilié', get: (row) => row.date_resilie },
      );
    }
    return columns;
  }, [phase, showMiseEnLigne, showResiliationDates]);

  const stepTrack = steps.length ? ` repeat(${steps.length}, 60px)` : '';
  const datesWidth = dateColumns.reduce((sum, column) => sum + (column.width ?? 104), 0);
  const dateTrack = dateColumns.length
    ? ` ${dateColumns.map((column) => `${column.width ?? 104}px`).join(' ')}`
    : '';
  const salariesWidth = showSalaries ? 88 : 0;
  const actionsWidth = steps.length ? 188 : 148;
  const gridTemplate = `minmax(180px, 1fr) 84px${showSalaries ? ' 88px' : ''}${stepTrack}${dateTrack} ${actionsWidth}px`;
  const minWidth = 32 + 180 + 84 + salariesWidth + (steps.length * 60) + datesWidth + actionsWidth;

  const toggleStep = async (adherent, stepCode) => {
    const key = `${adherent.id}:${stepCode}`;
    if (pendingKeys.has(key)) return;
    const next = isStepDone(statusOf(adherent, stepCode)) ? 'pending' : 'ok';
    setStatusOverrides((prev) => ({ ...prev, [key]: next }));
    setPendingKeys((prev) => new Set(prev).add(key));
    try {
      await klubService.updateWorkflowStep(adherent.id, stepCode, { status: next });
      await onChanged?.();
    } catch (err) {
      setStatusOverrides((prev) => {
        const copy = { ...prev };
        delete copy[key];
        return copy;
      });
      notify.error(err.message || 'Impossible de mettre à jour l’étape');
    } finally {
      setPendingKeys((prev) => {
        const copy = new Set(prev);
        copy.delete(key);
        return copy;
      });
    }
  };

  const detailUrl = (adherent) => ROUTES.klub.adherentDetail(adherent.id);
  const dolibarrUrl = (adherent) => `${ROUTES.klub.adherentDetail(adherent.id)}?tab=infos#dolibarr`;

  const checkAllSteps = async (adherent) => {
    const key = `${adherent.id}:all`;
    if (pendingKeys.has(key) || steps.length === 0) return;
    const pending = steps.filter((step) => !isStepDone(statusOf(adherent, step.step_code)));
    if (pending.length === 0) return;
    const overrides = {};
    pending.forEach((step) => {
      overrides[`${adherent.id}:${step.step_code}`] = 'ok';
    });
    setStatusOverrides((prev) => ({ ...prev, ...overrides }));
    setPendingKeys((prev) => new Set(prev).add(key));
    try {
      await klubService.updateWorkflow(
        adherent.id,
        pending.map((step) => ({ step_code: step.step_code, status: 'ok' })),
      );
      await onChanged?.();
    } catch (err) {
      setStatusOverrides((prev) => {
        const copy = { ...prev };
        pending.forEach((step) => {
          delete copy[`${adherent.id}:${step.step_code}`];
        });
        return copy;
      });
      notify.error(err.message || 'Impossible de cocher les étapes');
    } finally {
      setPendingKeys((prev) => {
        const copy = new Set(prev);
        copy.delete(key);
        return copy;
      });
    }
  };

  const renderStepControl = (adherent, step, variant) => {
    const face = faceOf(step);
    const done = isStepDone(statusOf(adherent, step.step_code));
    const key = `${adherent.id}:${step.step_code}`;
    const tip = stepTip(step, done);
    return (
      <button
        type="button"
        className={variant === 'tile' ? `ksl__tile ${done ? 'is-ok' : 'is-ko'}` : `ksl__sq ${done ? 'is-ok' : 'is-ko'}`}
        title={tip}
        aria-pressed={done}
        aria-label={tip}
        disabled={pendingKeys.has(key)}
        onClick={() => toggleStep(adherent, step.step_code)}
      >
        {variant === 'tile' ? face.mid : (done ? '✓' : '')}
      </button>
    );
  };

  if (loading) {
    return <AppSpinner />;
  }
  if (adherents.length === 0) {
    return <AppEmptyState icon="mdi:account-group-outline" message="Aucun site" />;
  }

  return (
    <>
      <KslFrame
        columns={gridTemplate}
        minWidth={`${minWidth}px`}
        header={(
          <>
            {steps.length > 0 && (
              <div className="ksl__row ksl__groups">
                <div />
                <div />
                {showSalaries ? <div /> : null}
                {groups.map((group) => (
                  <div
                    key={group.key}
                    className="ksl__group"
                    style={{ gridColumn: `span ${group.steps.length}` }}
                  >
                    {group.hideDesktopLabel ? '' : group.label}
                  </div>
                ))}
                {dateColumns.map((column) => <div key={column.key} />)}
                <div />
              </div>
            )}
            <div className="ksl__row ksl__head">
              <div>Entreprise</div>
              <div>Commercial</div>
              {showSalaries ? <div className="ksl__head-date">Salariés</div> : null}
              {steps.map((step) => {
                const face = faceOf(step);
                return (
                  <div key={step.step_code} className="ksl__head-step" title={step.label}>
                    <span>{face.mini}</span>
                    <small>{koCounts[step.step_code] ?? 0}</small>
                  </div>
                );
              })}
              {dateColumns.map((column) => (
                <div key={column.key} className="ksl__head-date" title={column.title ?? column.label}>{column.label}</div>
              ))}
              <div />
            </div>
          </>
        )}
      >
        {sortedRows.map((adherent) => {
                const doliValidated = adherent.dolibarr_match_status === 'validated';
                const formule = FORMULE_LABELS[adherent.formule] ?? adherent.formule;
                const allDone = steps.length > 0 && steps.every((step) => isStepDone(statusOf(adherent, step.step_code)));
                return (
                  <div key={adherent.id} className="ksl__row ksl__client">
                    <div className="ksl__name">
                      <b title={adherent.entreprise}>{adherent.entreprise}</b>
                      {formule ? <span className="ksl__plan">{formule}</span> : null}
                    </div>
                    <div className="ksl__commercial" title={adherent.commercial?.nom || undefined}>
                      {adherent.commercial?.nom || '—'}
                    </div>
                    {showSalaries ? (
                      <div className="ksl__date">{adherent.nombre_salaries ?? '—'}</div>
                    ) : null}
                    {steps.map((step) => (
                      <div key={step.step_code} className="ksl__cell">
                        {renderStepControl(adherent, step, 'sq')}
                      </div>
                    ))}
                    {dateColumns.map((column) => (
                      <div key={column.key} className="ksl__date" title={column.title}>{formatDate(column.get(adherent))}</div>
                    ))}
                    <div className="ksl__actions">
                      {steps.length > 0 && (
                        <button
                          type="button"
                          className="ksl__btn ksl__btn--sec"
                          title="Cocher toutes les étapes"
                          aria-label="Cocher toutes les étapes"
                          disabled={allDone || pendingKeys.has(`${adherent.id}:all`)}
                          onClick={() => checkAllSteps(adherent)}
                        >
                          Tout
                        </button>
                      )}
                      <Link className="ksl__btn" to={detailUrl(adherent)} state={{ listReturnTo }}>Voir</Link>
                      <Link
                        className={`ksl__btn ksl__btn--doli ${doliValidated ? 'is-ok' : 'is-ko'}`}
                        to={dolibarrUrl(adherent)}
                        state={{ listReturnTo }}
                        title={dolibarrTitle(adherent)}
                      >
                        Doli
                      </Link>
                    </div>
                  </div>
                );
              })}
      </KslFrame>

      <div className="ksl__cards">
            {sortedRows.map((adherent) => {
              const koCount = steps.filter((step) => !isStepDone(statusOf(adherent, step.step_code))).length;
              const formule = FORMULE_LABELS[adherent.formule] ?? adherent.formule;
              const dateBits = groups.length === 0
                ? []
                : dateColumns
                  .filter((column) => column.get(adherent))
                  .map((column) => `${column.label} ${formatDate(column.get(adherent))}`);
              const sub = [
                formule,
                adherent.commercial?.nom,
                showSalaries && adherent.nombre_salaries != null ? `${adherent.nombre_salaries} sal.` : null,
                ...dateBits,
              ].filter(Boolean).join(' · ');
              return (
                <article key={adherent.id} className="ksl-card">
                  <div className="ksl-card__top">
                    <div className="ksl-card__id">
                      <b title={adherent.entreprise}>{adherent.entreprise}</b>
                      {sub ? <span title={sub}>{sub}</span> : null}
                    </div>
                    {koCount > 0 ? <span className="ksl__kopill">{koCount} KO</span> : null}
                    {steps.length > 0 && (
                      <button
                        type="button"
                        className="ksl__btn ksl__btn--sec"
                        title="Cocher toutes les étapes"
                        aria-label="Cocher toutes les étapes"
                        disabled={koCount === 0 || pendingKeys.has(`${adherent.id}:all`)}
                        onClick={() => checkAllSteps(adherent)}
                      >
                        Tout
                      </button>
                    )}
                    <Link
                      className={`ksl__btn ksl__btn--doli ${adherent.dolibarr_match_status === 'validated' ? 'is-ok' : 'is-ko'}`}
                      to={dolibarrUrl(adherent)}
                      state={{ listReturnTo }}
                      title={dolibarrTitle(adherent)}
                    >
                      Doli
                    </Link>
                    <Link
                      className="ksl__chev"
                      to={detailUrl(adherent)}
                      state={{ listReturnTo }}
                      aria-label={`Voir ${adherent.entreprise}`}
                    >
                      ›
                    </Link>
                  </div>
                  {groups.length === 0 && dateColumns.length > 0 && (
                    <div className="ksl-card__dates">
                      {dateColumns.map((column) => (
                        <div key={column.key}>
                          <span>{column.title ?? column.label}</span>
                          <b>{formatDate(column.get(adherent))}</b>
                        </div>
                      ))}
                    </div>
                  )}
                  {groups.length > 0 && phaseRows.map((row) => (
                    <div key={row.map((group) => group.key).join('-')} className="ksl-card__prow">
                      {row.map((group) => (
                        <div key={group.key} className="ksl-card__phase" style={{ flex: group.steps.length }}>
                          <span>{group.label}</span>
                          <div className="ksl-card__tiles">
                            {group.steps.map((step) => (
                              <React.Fragment key={step.step_code}>
                                {renderStepControl(adherent, step, 'tile')}
                              </React.Fragment>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </article>
              );
            })}
      </div>
    </>
  );
};

export default KlubSiteList;
